import { DeleteStudentDialog } from '@/components/students/DeleteStudentDialog';
import { ImportStudentsDialog } from '@/components/students/ImportStudentsDialog';
import { StudentDialog } from '@/components/students/StudentDialog';
import Page, {
  PageHeader,
  PageHeaderActions,
  PageHeaderContent,
  PageHeaderDescription,
  PageHeaderTitle,
} from '@/components/Page';
import {
  useCreateStudent,
  useDeleteStudent,
  useStudents,
  useUpdateStudent,
  type Student,
} from '@/hooks/useStudents';
import type { AppContext } from '@/main';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Button } from '@ui/components/button';
import { Edit2, FileUp, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Listing } from '@/components/Listing';
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import {
  AvatarBeam,
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ui/components';
import { format } from 'date-fns';
import { usePaginationState } from '@/hooks/usePaginationState';
import { useNavigate } from '@tanstack/react-router';

export const Route = createFileRoute('/_dashboard/students/')<AppContext>({
  component: RouteComponent,
  loader: () => ({
    crumb: 'Students',
  }),
});

function RouteComponent() {
  const { pagination, setPagination, orderDirection, setOrderDirection, orderBy, setOrderBy } =
    usePaginationState<'name' | 'createdAt' | 'updatedAt'>({
      defaultOrder: 'name',
    });
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | undefined>(undefined);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: null as string | null,
    name: '',
  });

  const {
    data: studentsResponse,
    isLoading,
    error,
  } = useStudents({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    orderBy,
    orderDirection,
  });
  const students = studentsResponse?.items || [];

  const createStudentMutation = useCreateStudent();
  const updateStudentMutation = useUpdateStudent();
  const deleteStudentMutation = useDeleteStudent();

  const handleCreateStudent = async (data: { name: string }) => {
    await createStudentMutation.mutateAsync(data);
  };

  const handleUpdateStudent = async (data: { name: string }) => {
    if (!editingStudent) return;
    await updateStudentMutation.mutateAsync({
      studentId: editingStudent.id,
      data,
    });
  };

  const handleDeleteStudent = async (studentId: string) => {
    await deleteStudentMutation.mutateAsync(studentId);
  };

  const table = useStudentsTable({
    setDeleteDialog: setDeleteDialog,
    setEditing: setEditingStudent,
    students,
    pagination,
    setPagination,
    rowCount: studentsResponse?.totalItems || 0,
  });

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-destructive">Failed to load students</div>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <Page>
      <PageHeader>
        <PageHeaderContent>
          <PageHeaderTitle>Students</PageHeaderTitle>
          <PageHeaderDescription>
            Manage your students and their enrollment
            {studentsResponse && ` (${studentsResponse.totalItems} total)`}
          </PageHeaderDescription>
        </PageHeaderContent>
        <PageHeaderActions>
          <Button onClick={() => setIsImportDialogOpen(true)} variant="outline" className="gap-2">
            <FileUp className="h-4 w-4" />
            Import
          </Button>
          <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Student
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <Listing.Root
        values={students}
        CardComponent={StudentCard}
        table={table}
        isLoading={isLoading}
        totalItems={studentsResponse?.totalItems || 0}
        onDelete={(item: Student) => {
          setDeleteDialog({
            open: true,
            id: item.id,
            name: item.name,
          });
        }}
        onEdit={(item: Student) => {
          setEditingStudent(item);
        }}
      >
        <Listing.Header.Root>
          <Listing.Header.StatusFilter
            active="all"
            options={[
              {
                label: 'All Students',
                value: 'all',
              },
            ]}
            default="all"
            onChange={() => {}}
          />
          <Listing.Header.Selector.Root>
            <Listing.Header.Selector.View />
            <Listing.Header.Selector.OrderBy
              default="name"
              onChange={(value) => {
                const [orderBy, orderDirection] = value.split('-');
                setOrderBy(orderBy as 'name' | 'createdAt' | 'updatedAt');
                setOrderDirection(orderDirection as 'asc' | 'desc');
              }}
              options={[
                {
                  label: 'Name',
                  value: 'name',
                },
                {
                  label: 'Created At',
                  value: 'createdAt',
                },
                {
                  label: 'Updated At',
                  value: 'updateAt',
                },
              ]}
              orderBy={orderBy}
              orderDirection={orderDirection}
            />
            <Listing.Header.Selector.PageSize
              value={pagination.pageSize.toString()}
              onChange={(value) => setPagination({ ...pagination, pageSize: parseInt(value, 10) })}
            />
          </Listing.Header.Selector.Root>
        </Listing.Header.Root>
      </Listing.Root>

      {/* Dialogs */}
      <StudentDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onSave={handleCreateStudent}
      />

      <StudentDialog
        open={!!editingStudent}
        onOpenChange={(open) => !open && setEditingStudent(undefined)}
        student={editingStudent}
        onSave={handleUpdateStudent}
      />

      <ImportStudentsDialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen} />

      <DeleteStudentDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        studentId={deleteDialog.id}
        studentName={deleteDialog.name}
        onDelete={handleDeleteStudent}
      />
    </Page>
  );
}

function StudentCard({ item }: { item: Student }) {
  const navigate = useNavigate();
  return (
    <Card
      className="flex-row cursor-pointer hover:bg-card/5 "
      onClick={() =>
        navigate({
          to: `/students/$studentId`,
          params: {
            studentId: item.id,
          },
        })
      }
    >
      <CardHeader>
        <AvatarBeam id={item.id} name={item.name} />
      </CardHeader>
      <CardContent className="text-ellipsis overflow-hidden whitespace-nowrap">
        <CardTitle>{item.name}</CardTitle>
        <CardDescription>
          <small>{item.id}</small>
        </CardDescription>
      </CardContent>
    </Card>
  );
}

function useStudentsTable({
  students,
  setEditing,
  setDeleteDialog,
  pagination,
  setPagination,
  rowCount,
}: {
  students: Student[];
  setEditing: (course: Student) => void;
  setDeleteDialog: (params: { open: boolean; id: string | null; name: string }) => void;
  pagination: PaginationState;
  setPagination: Dispatch<SetStateAction<PaginationState>>;
  rowCount: number;
}) {
  const columns = useMemo<ColumnDef<Student>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row: { original: item } }) => (
          <Link
            to="/courses/$courseId"
            params={{ courseId: item.id }}
            className="font-medium hover:underline"
          >
            {item.name}
          </Link>
        ),
      },
      {
        accessorKey: 'exams',
        header: 'Exams',
        cell: ({ row: { original: item } }) => (
          <Badge variant="secondary" className="font-mono">
            {item.exams?.length ?? '0'}
          </Badge>
        ),
      },
      {
        accessorKey: 'archived',
        header: 'Status',
        cell: ({
          row: {
            original: { archived },
          },
        }) => (
          <Badge variant={archived ? 'outline' : 'default'}>
            {archived ? 'Archived' : 'Active'}
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created at',
        cell: ({ row: { original: item } }) => (
          <div className="text-muted-foreground">
            {format(new Date(item.createdAt), 'MMM dd, yyyy')}
          </div>
        ),
      },
      {
        id: 'actions',
        enableHiding: false,
        header: 'Actions',
        cell: ({ row: { original: item } }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => {
                  setEditing(item);
                }}
              >
                <Edit2 className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  console.log('Delete exam');
                  if (item) {
                    setDeleteDialog({
                      open: true,
                      id: item.id,
                      name: item.name,
                    });
                  }
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: students,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
    manualPagination: true,
    rowCount,
  });

  return table;
}
